import React from "react";
import { useTranslation } from "react-i18next";
import { RootState, useDispatch, useSelector } from "src/store";

import { useGroupFiles } from "@dashboard/files/api/helpers";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { ConfirmDialog, DropdownItem } from "@devices/components/shared";
import DropdownContainer from "@devices/components/shared/Dropdown/DropdownContainer";
import { deleteGroup, updateGroupMembers } from "@devices/store";
import { Group } from "@devices/types";
import { Box, DialogContentText, Typography, useTheme } from "@mui/material";
import { selectProductsByGroupId } from "@products/store/selectors/products";
import AuthService from "@services/authService";
import { PermissionService } from "@services/permissionService/permissionService";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const DeleteGroupItem = (props: Group & { setIsDeleteGroupDialogOpen: (open: boolean) => void }) => {
    const { t } = useTranslation(["sidepanels", "modals", "common"]);

    const { data: workspace } = useCurrentWorkspace();
    const isDefaultGroup = workspace?.defaultGroupId === props.id;

    const { groupAbility } = PermissionService();
    const groups = useSelector(selectGroups);
    const hasChildrenGroups = groups.some((item) => item.parentId === props.id);

    const handleDeleteGroup = () => {
        props.setIsDeleteGroupDialogOpen(true);
    };

    let tooltipMessage = "";

    if (isDefaultGroup) {
        tooltipMessage = t("sidepanels:groupPanel.tooltip.defaultGroupCannotBeDeleted");
    } else if (groupAbility(props, workspace).cannot("delete", "Group")) {
        tooltipMessage = t("sidepanels:groupPanel.tooltip.noPermissionToDeleteGroup");
    } else if (hasChildrenGroups) {
        tooltipMessage = t("sidepanels:groupPanel.tooltip.groupWithNestedGroups");
    }

    const isDeleteDisabled = hasChildrenGroups || isDefaultGroup || groupAbility(props, workspace).cannot("delete", "Group");

    return (
        <DropdownItem
            disabled={isDeleteDisabled}
            iconName="fa-solid fa-fw fa-trash-alt"
            label={t("common:button.delete")}
            onClick={handleDeleteGroup}
            tooltipProps={{ title: tooltipMessage }}
        />
    );
};

const LeaveGroupItem = (props: Group & { setIsLeaveGroupDialogOpen: (open: boolean) => void }) => {
    const { t } = useTranslation(["sidepanels", "modals", "common"]);
    const currentUser = AuthService.getCurrentUser();

    const isGroupMember = props.members.some((item) => item.email === currentUser?.email);

    const handleLeaveGroup = () => {
        props.setIsLeaveGroupDialogOpen(true);
    };

    if (!isGroupMember) {
        return (
            <DropdownItem
                label={t("sidepanels:groupPanel.button.leaveGroup")}
                iconName="fa-solid fa-right-from-bracket"
                tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.notDirectMemberOfGroup") }}
                disabled
            />
        );
    }

    return (
        <DropdownItem
            label={t("sidepanels:groupPanel.button.leaveGroup")}
            iconName="fa-solid fa-right-from-bracket"
            onClick={handleLeaveGroup}
        />
    );
};

export default (props: Group): React.JSX.Element => {
    const [isLeaveGroupDialogOpen, setIsLeaveGroupDialogOpen] = React.useState(false);
    const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = React.useState(false);

    const anchorRef = React.useRef<HTMLButtonElement | null>(null);

    const theme = useTheme();
    const dispatch = useDispatch();
    const currentUser = AuthService.getCurrentUser();
    const { t } = useTranslation(["sidepanels", "modals", "common"]);

    const { data } = useGroupFiles(props.id);
    const filesLength = data?.count || 0;

    const groupProducts = useSelector((state: RootState) => selectProductsByGroupId(state, props.id));
    const licensesLength = props.licenseIds.length;
    const productsLength = groupProducts.length;
    const isGroupEmpty = !licensesLength && !productsLength && !filesLength;

    const cancelLeaveGroupDialog = () => {
        if (isLeaveGroupDialogOpen) {
            setIsLeaveGroupDialogOpen(false);
        }
    };
    const leaveGroup = async () => {
        if (currentUser) {
            await dispatch(updateGroupMembers({ id: props.id, removedMembers: [currentUser?.email] }))
                .unwrap()
                .then(() => showSuccessToast(t("sidepanels:groupPanel.toast.memberUpdated")))
                .catch(({ message }) => showErrorToast(message));
        }
    };
    const cancelDeleteGroupDialog = () => {
        if (isDeleteGroupDialogOpen) {
            setIsDeleteGroupDialogOpen(false);
        }
    };
    const confirmDeleteGroup = async () => {
        await dispatch(deleteGroup({ id: props.id }))
            .unwrap()
            .then(() => showSuccessToast(t("sidepanels:groupPanel.toast.groupDeletedSuccess")))
            .catch(({ message = t("sidepanels:groupPanel.toast.groupDeletedError") }) => {
                showErrorToast(message);
                setIsDeleteGroupDialogOpen(false);
            });
    };

    return (
        <React.Fragment>
            <DropdownContainer icon={<i className="fas fa-ellipsis-v" />} anchorRef={anchorRef}>
                <DeleteGroupItem {...props} setIsDeleteGroupDialogOpen={setIsDeleteGroupDialogOpen} />
                <LeaveGroupItem {...props} setIsLeaveGroupDialogOpen={setIsLeaveGroupDialogOpen} />
            </DropdownContainer>

            <ConfirmDialog
                title={t("modals:deleteGroup.title")}
                open={isDeleteGroupDialogOpen}
                onConfirm={confirmDeleteGroup}
                onConfirmDisabled={!isGroupEmpty}
                dangerButton
                primaryActionText={t("common:button.delete")}
                onClose={cancelDeleteGroupDialog}
            >
                {isGroupEmpty ? (
                    <Typography>{t("modals:deleteGroup.description")}</Typography>
                ) : (
                    <React.Fragment>
                        <DialogContentText variant="body1" style={{ marginBottom: theme.spacing(4) }}>
                            {t("modals:deleteGroup.cannotDeleteReason")}
                        </DialogContentText>
                        <Box ml={theme.spacing(4)}>
                            <ul>
                                {licensesLength ? (
                                    <li>
                                        <Typography variant="body1">
                                            <b>{t("modals:deleteGroup.licenseCount", { count: licensesLength })}</b>
                                        </Typography>
                                    </li>
                                ) : null}
                                {productsLength ? (
                                    <li>
                                        <Typography variant="body1">
                                            <b>{t("modals:deleteGroup.productCount", { count: productsLength })}</b>
                                        </Typography>
                                    </li>
                                ) : null}
                                {filesLength ? (
                                    <li>
                                        <Typography variant="body1">
                                            <b>{t("modals:deleteGroup.fileCount", { count: filesLength })}</b>
                                        </Typography>
                                    </li>
                                ) : null}
                            </ul>
                        </Box>

                        <DialogContentText variant="body1" style={{ marginTop: theme.spacing(4) }}>
                            {t("modals:deleteGroup.reassignOrRemoveItems")}
                        </DialogContentText>
                    </React.Fragment>
                )}
            </ConfirmDialog>

            <ConfirmDialog
                title={t("modals:leaveGroup.title")}
                open={isLeaveGroupDialogOpen}
                onConfirm={leaveGroup}
                onClose={cancelLeaveGroupDialog}
            >
                <DialogContentText>{t("modals:leaveGroup.description")}</DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
